import { toast } from 'sonner';
import type { NoteFile } from '../types/case';
import type { AdminRole } from '../types/config';
import { useStore } from '../contexts/StoreContext';
import { ADMIN_USERS } from '../data/users';
import { availableForNote } from '../utils/caseStatus';
import { buildNoteBody, combinedFileNo, fileDivision } from '../utils/noteSheet';
import { bn, nowIso } from '../utils/bn';

export type FileDecision = 'approved' | 'returned' | 'rejected' | 'held';

const DECISION_LABEL: Record<FileDecision, string> = {
  approved: 'চূড়ান্ত অনুমোদন',
  returned: 'সংশোধনের জন্য ফেরত',
  rejected: 'অনুমোদন প্রত্যাখ্যান',
  held: 'স্থগিত',
};

/** Combined e-file (note sheet) workflow: create → edit → forward → final approval. */
export function useNoteFiles(role: AdminRole) {
  const { cases, noteFiles, config, addNoteFile, updateNoteFile, updateCase } = useStore();
  const user = ADMIN_USERS[role];
  const by = { actor: user.name, role: user.title };
  const approver = config.approvalLevels.find((l) => l.role === 'approver' && l.enabled)?.officer ?? 'অনুমোদনকারী কর্মকর্তা';
  const preparer = config.approvalLevels.find((l) => l.role === 'dealing' && l.enabled)?.officer ?? 'নথি প্রস্তুতকারী';
  const casesOf = (pcrs: string[]) => cases.filter((c) => pcrs.includes(c.pcr));
  const subjectFor = (n: number) => (n > 1 ? `আটককৃত ${bn(n)}টি যানবাহন একত্রে অবমুক্তির অনুমোদন প্রসঙ্গে।` : 'আটককৃত যানবাহন অবমুক্তির অনুমোদন প্রসঙ্গে।');

  return {
    create(pcrs: string[]): string | null {
      const list = casesOf(pcrs).filter(availableForNote);
      if (!list.length) { toast.error('নোটশিটে অন্তর্ভুক্ত করার মতো কোনো যাচাইকৃত ও পরিশোধিত PCR নির্বাচিত হয়নি।'); return null; }
      const seq = noteFiles.reduce((m, f) => Math.max(m, Number(f.id.slice(3))), 400) + 1;
      const id = `NF-${seq}`;
      const at = nowIso();
      const file: NoteFile = {
        id, fileNo: combinedFileNo(seq), subject: subjectFor(list.length), division: fileDivision(list),
        pcrs: list.map((c) => c.pcr), bodyHtml: buildNoteBody(list, config),
        observation: 'তফসিলভুক্ত প্রতিটি কেসের দাখিলকৃত কাগজপত্র মূল কপির সাথে মিলিয়ে দেখা হয়েছে এবং জরিমানা পরিশোধ সমন্বিত পাওয়া গেছে।',
        recommendation: 'সদয় অনুমোদনের জন্য পেশ করা হলো।',
        attachments: ['তফসিল-“ক”: অন্তর্ভুক্ত PCR তালিকা', 'প্রতিটি PCR-এর যাচাইকৃত কাগজপত্র', 'পেমেন্ট নিশ্চিতকরণ / সরকারি চালান'],
        status: 'draft', createdAt: at, createdBy: user.name, savedAt: at, forwards: [], excluded: [],
      };
      addNoteFile(file);
      list.forEach((c) => updateCase(c.pcr, (x) => ({ ...x, noteFileId: id, approval: 'drafting' }), { ...by, key: 'note_saved', action: `সমন্বিত নোটশিট ${file.fileNo}-এ অন্তর্ভুক্ত` }));
      toast.success(`${bn(list.length)}টি PCR নিয়ে সমন্বিত নোটশিট তৈরি হয়েছে।`);
      return id;
    },
    save(id: string, patch: Partial<Pick<NoteFile, 'subject' | 'bodyHtml' | 'observation' | 'recommendation' | 'attachments'>>, silent = false) {
      updateNoteFile(id, (f) => ({ ...f, ...patch, savedAt: nowIso() }));
      if (!silent) toast.success('খসড়া নোটশিট সংরক্ষিত হয়েছে।');
    },
    regenerate(id: string) {
      const f = noteFiles.find((x) => x.id === id);
      if (!f) return;
      updateNoteFile(id, (x) => ({ ...x, bodyHtml: buildNoteBody(casesOf(x.pcrs), config), subject: subjectFor(x.pcrs.length), division: fileDivision(casesOf(x.pcrs)), savedAt: nowIso() }));
      toast.success('PCR তথ্য থেকে নোট পুনরায় তৈরি হয়েছে।');
    },
    addPcrs(id: string, pcrs: string[]) {
      const add = casesOf(pcrs).filter(availableForNote);
      if (!add.length) return;
      const f = noteFiles.find((x) => x.id === id);
      if (!f) return;
      const all = [...f.pcrs, ...add.map((c) => c.pcr)];
      updateNoteFile(id, (x) => ({ ...x, pcrs: all, bodyHtml: buildNoteBody(casesOf(all), config), subject: subjectFor(all.length), division: fileDivision(casesOf(all)), savedAt: nowIso() }));
      add.forEach((c) => updateCase(c.pcr, (x) => ({ ...x, noteFileId: id, approval: 'drafting' }), { ...by, key: 'note_saved', action: `সমন্বিত নোটশিট ${f.fileNo}-এ অন্তর্ভুক্ত` }));
      toast.success(`${bn(add.length)}টি PCR নোটশিটে যুক্ত হয়েছে।`);
    },
    removePcr(id: string, pcr: string) {
      const f = noteFiles.find((x) => x.id === id);
      if (!f) return;
      if (f.pcrs.length === 1) { toast.error('নোটশিটে অন্তত একটি PCR থাকতে হবে।'); return; }
      const rest = f.pcrs.filter((p) => p !== pcr);
      updateNoteFile(id, (x) => ({ ...x, pcrs: rest, bodyHtml: buildNoteBody(casesOf(rest), config), subject: subjectFor(rest.length), division: fileDivision(casesOf(rest)), savedAt: nowIso() }));
      updateCase(pcr, (x) => ({ ...x, noteFileId: undefined, approval: 'drafting' }), { ...by, key: 'note_saved', action: `নোটশিট ${f.fileNo} থেকে বাদ — পুনরায় প্রস্তুত তালিকায়` });
      toast.success('PCR নোটশিট থেকে বাদ দেওয়া হয়েছে।');
    },
    forward(id: string, remark: string) {
      const f = noteFiles.find((x) => x.id === id);
      if (!f) return;
      const at = nowIso();
      const text = remark || 'সদয় অনুমোদনের জন্য পেশ করা হলো।';
      updateNoteFile(id, (x) => ({ ...x, status: 'pending', savedAt: at, forwards: [...x.forwards, { at, from: user.name, fromRole: user.title, to: approver, action: f.status === 'returned' ? 'সংশোধিত নথি পুনঃঅগ্রায়ন' : 'অগ্রায়ন', remark: text }] }));
      f.pcrs.forEach((p) => updateCase(p, (x) => ({ ...x, approval: 'pending', approvalComment: undefined }), { ...by, key: 'forwarded', action: `সমন্বিত নোটশিট ${f.fileNo} অনুমোদনের জন্য অগ্রায়ন`, note: text }));
      toast.success(`নোটশিট ${approver}-এর নিকট অগ্রায়ন করা হয়েছে।`);
    },
    decide(id: string, decision: FileDecision, comment: string, excluded: { pcr: string; reason: string }[] = []) {
      const f = noteFiles.find((x) => x.id === id);
      if (!f) return;
      const at = nowIso();
      const exPcrs = decision === 'approved' ? excluded.map((e) => e.pcr) : [];
      const kept = f.pcrs.filter((p) => !exPcrs.includes(p));
      const to = decision === 'approved' ? 'অবমুক্তি পত্র শাখা' : preparer;
      const text = comment || DECISION_LABEL[decision];
      updateNoteFile(id, (x) => ({
        ...x, status: decision, pcrs: kept, excluded: [...x.excluded, ...(decision === 'approved' ? excluded : [])],
        decidedAt: at, decidedBy: user.name, comment: text,
        forwards: [...x.forwards, { at, from: user.name, fromRole: user.title, to, action: DECISION_LABEL[decision], remark: text + (exPcrs.length ? ` (অনুমোদন থেকে বাদ: ${exPcrs.join(', ')})` : '') }],
      }));
      const caseApproval = decision === 'approved' ? 'approved' : decision;
      kept.forEach((p) => updateCase(p, (x) => ({ ...x, approval: caseApproval, approvalComment: decision === 'approved' ? undefined : text, noteFileNo: decision === 'approved' ? f.fileNo : x.noteFileNo }),
        { ...by, key: decision === 'approved' ? 'approved' : decision === 'held' ? 'held' : decision, action: `নোটশিট ${f.fileNo} — ${DECISION_LABEL[decision]}`, note: comment || undefined }));
      excluded.filter((e) => exPcrs.includes(e.pcr)).forEach((e) => updateCase(e.pcr, (x) => ({ ...x, approval: 'returned', approvalComment: e.reason, noteFileId: undefined }),
        { ...by, key: 'returned', action: `নোটশিট ${f.fileNo}-এর অনুমোদন থেকে বাদ — সংশোধনের জন্য ফেরত`, note: e.reason }));
      toast.success(decision === 'approved' ? `${bn(kept.length)}টি PCR চূড়ান্ত অনুমোদিত — অবমুক্তি পত্র প্রস্তুতের জন্য প্রস্তুত।` : `নোটশিট: ${DECISION_LABEL[decision]} — ইতিহাস হালনাগাদ হয়েছে।`);
    },
    comment(id: string, text: string) {
      const at = nowIso();
      updateNoteFile(id, (x) => ({ ...x, forwards: [...x.forwards, { at, from: user.name, fromRole: user.title, to: 'নথি', action: 'মন্তব্য', remark: text }] }));
      toast.success('মন্তব্য নোটশিটে সংযুক্ত হয়েছে।');
    },
  };
}
